import { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../lib/apiClient';
import { useCustomers } from '../context/CustomerContext';
import { useAuth } from '../context/AuthContext';
import { fmtTs } from '../lib/globalTz';


interface SpiderFootTabProps {
  fixedCustomerId?: string;
  hideCustomerSelector?: boolean;
}

interface SpiderFootScan {
  id: number;
  customer_id: string;
  target: string;
  target_type: string;
  profile: string;
  spiderfoot_scan_id?: string;
  status: string;
  requested_by_email?: string;
  result_summary?: {
    total?: number;
    byType?: Record<string, number>;
    byModule?: Record<string, number>;
  };
  ai_analysis?: SpiderFootAiAnalysis | null;
  ai_analyzed_at?: string | null;
  error?: string;
  created_at: string;
  updated_at: string;
}

interface SpiderFootConfig {
  enabled: boolean;
  configured: boolean;
  baseUrlConfigured: boolean;
  authConfigured: boolean;
}

interface SpiderFootAiFinding {
  title: string;
  data: string;
  type?: string;
  module?: string;
  confidence: number;
  riskLevel: 'low' | 'medium' | 'high' | 'critical';
  reasons: string[];
  recommendedAction: string;
}

interface SpiderFootAiCollectionTarget {
  target: string;
  targetType: string;
  recommendedProfile: string;
  rationale: string;
}

interface SpiderFootAiAnalysis {
  overallConfidence: number;
  riskLevel: 'low' | 'medium' | 'high' | 'critical';
  executiveSummary: string;
  keyFindings: SpiderFootAiFinding[];
  exposureThemes: string[];
  correlatedIndicators: string[];
  additionalCollectionTargets: SpiderFootAiCollectionTarget[];
  recommendedNextSteps: string[];
  falsePositiveNotes: string[];
  model?: string;
  generatedAt?: string;
}

const TARGET_TYPES = [
  { value: 'domain', label: 'Domain' },
  { value: 'hostname', label: 'Hostname' },
  { value: 'ip', label: 'IP Address' },
  { value: 'email', label: 'Email' },
  { value: 'url', label: 'URL' },
  { value: 'username', label: 'Username' },
];

const PROFILES = [
  { value: 'passive', label: 'Passive OSINT' },
  { value: 'domain-osint', label: 'Domain OSINT' },
  { value: 'ip-osint', label: 'IP OSINT' },
  { value: 'breach-osint', label: 'Breach OSINT' },
];

function cleanSpiderFootStatus(value?: string): string {
  const raw = String(value ?? '').trim();

  if (!raw) return 'Unknown';

  if (
    raw.length > 80 ||
    raw.includes('function(') ||
    raw.includes('use strict') ||
    raw.includes('TARGET_WEB_CONTENT') ||
    raw.includes('<html') ||
    raw.includes('<!DOCTYPE')
  ) {
    return 'Unknown';
  }

  const lower = raw.toLowerCase();

  if (['submitted', 'submitting', 'created', 'queued', 'new'].includes(lower)) return 'Submitted';
  if (['running', 'run', 'started', 'starting', 'scanning', 'active'].includes(lower)) return 'Running';
  if (['finished', 'complete', 'completed', 'done', 'success', 'succeeded'].includes(lower)) return 'Completed';
  if (['failed', 'error', 'aborted', 'exception'].includes(lower)) return 'Failed';
  if (['stopped', 'cancelled', 'canceled', 'stop'].includes(lower)) return 'Stopped';

  return raw;
}

function spiderFootStatusClass(value?: string): string {
  const status = cleanSpiderFootStatus(value).toLowerCase();

  if (status === 'running') {
    return 'bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300';
  }

  if (status === 'completed') {
    return 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300';
  }

  if (status === 'failed') {
    return 'bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300';
  }

  if (status === 'stopped') {
    return 'bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300';
  }

  return 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300';
}

function riskClass(value?: string): string {
  switch (String(value ?? '').toLowerCase()) {
    case 'critical':
      return 'bg-red-100 dark:bg-red-950/40 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-900';
    case 'high':
      return 'bg-orange-100 dark:bg-orange-950/40 text-orange-700 dark:text-orange-300 border border-orange-200 dark:border-orange-900';
    case 'medium':
      return 'bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-900';
    case 'low':
    default:
      return 'bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-900';
  }
}

function topCountsText(value?: Record<string, number>): string {
  if (!value) return '—';
  return Object.entries(value)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6)
    .map(([key, count]) => `${key}: ${count}`)
    .join(' · ') || '—';
}

export function SpiderFootTab({ fixedCustomerId, hideCustomerSelector = false }: SpiderFootTabProps = {}) {
  const { customers, activeCustomerId, activeCustomer, setActiveCustomerId } = useCustomers();
  const { canCapability } = useAuth();
  const canStartScan = canCapability('spiderfoot', 'start_scan');
  const canViewResults = canCapability('spiderfoot', 'view_results');
  const canDownloadResults = canCapability('spiderfoot', 'download_results');
  const canRunAiAnalysis = canCapability('spiderfoot', 'run_ai_analysis');

  const effectiveCustomerId = fixedCustomerId || activeCustomerId || customers[0]?.id || '';
  const [customerId, setCustomerId] = useState(effectiveCustomerId);
  const selectedCustomer = useMemo(
    () => customers.find((customer) => customer.id === customerId) || activeCustomer,
    [customers, customerId, activeCustomer],
  );
  const [target, setTarget] = useState('');
  const [targetType, setTargetType] = useState('domain');
  const [profile, setProfile] = useState('passive');
  const [config, setConfig] = useState<SpiderFootConfig | null>(null);
  const [scans, setScans] = useState<SpiderFootScan[]>([]);
  const [selectedResult, setSelectedResult] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(false);
  const [aiAnalyzingId, setAiAnalyzingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function loadConfig() {
    try {
      const data = await apiFetch<SpiderFootConfig>('/spiderfoot/config');
      setConfig(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function loadScans(nextCustomerId = customerId) {
    if (!nextCustomerId) {
      setScans([]);
      setError('Select a customer before loading OSINT scans.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await apiFetch<{ scans: SpiderFootScan[] }>(
        `/spiderfoot/scans?customerId=${encodeURIComponent(nextCustomerId)}`,
      );
      setScans(data.scans ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  async function startScan() {
    if (!canStartScan) {
      setError('Your role is not allowed to start OSINT scans.');
      return;
    }
    if (!customerId) {
      setError('Select a customer before starting an OSINT scan.');
      return;
    }

    setStarting(true);
    setError(null);
    setSelectedResult(null);

    try {
      await apiFetch('/spiderfoot/scans', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId, target, targetType, profile }),
      });

      setTarget('');
      await loadScans(customerId);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setStarting(false);
    }
  }

  async function refreshScan(scan: SpiderFootScan) {
    if (!canViewResults) {
      setError('Your role is not allowed to refresh OSINT scan status.');
      return;
    }
    setError(null);

    try {
      await apiFetch(`/spiderfoot/scans/${scan.id}/refresh`, {
        method: 'POST',
      });

      await loadScans(customerId);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

function downloadResults(scan: SpiderFootScan, format: 'csv' | 'json' = 'csv') {
  if (!canDownloadResults) {
    setError('Your role is not allowed to download OSINT results.');
    return;
  }
  const url = `/api/spiderfoot/scans/${scan.id}/download?format=${encodeURIComponent(format)}`;
  window.open(url, '_blank', 'noopener,noreferrer');
}

  async function loadResults(scan: SpiderFootScan) {
    if (!canViewResults) {
      setError('Your role is not allowed to view OSINT results.');
      return;
    }
    setError(null);
    setSelectedResult(null);

    try {
      const data = await apiFetch(`/spiderfoot/scans/${scan.id}/results`);
      setSelectedResult(data);
      await loadScans(customerId);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function runAiAnalysis(scan: SpiderFootScan) {
    if (!canRunAiAnalysis) {
      setError('Your role is not allowed to run OSINT AI analysis.');
      return;
    }

    setAiAnalyzingId(scan.id);
    setError(null);

    try {
      const data = await apiFetch<{ analysis: SpiderFootAiAnalysis; summary?: SpiderFootScan['result_summary']; aiAnalyzedAt?: string }>(`/spiderfoot/scans/${scan.id}/ai-analysis`, {
        method: 'POST',
      });

      setSelectedResult({
        scan,
        summary: data.summary ?? scan.result_summary,
        aiAnalysis: data.analysis,
        aiAnalyzedAt: data.aiAnalyzedAt,
      });
      await loadScans(customerId);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setAiAnalyzingId(null);
    }
  }

  useEffect(() => {
    const nextCustomerId = fixedCustomerId || activeCustomerId || customers[0]?.id || '';
    if (nextCustomerId && nextCustomerId !== customerId) {
      setCustomerId(nextCustomerId);
    }
  }, [fixedCustomerId, activeCustomerId, customers, customerId]);

  useEffect(() => {
    void loadConfig();
  }, []);

  useEffect(() => {
    if (customerId) void loadScans(customerId);
  }, [customerId]);

  return (
    <div className="slide-in space-y-4">
      <div className="bg-white dark:bg-card rounded-xl shadow p-5">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div>
            <h2 className="text-lg font-bold text-foreground">OSINT Reconnaissance</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Run controlled passive reconnaissance scans, review collected evidence, and use AI to prioritize exposure findings and next pivots.
            </p>
          </div>

          <div className="text-xs space-y-1">
            <div>
              Feature: {config?.enabled ? 'Enabled' : 'Disabled'}
            </div>
            <div>
              Endpoint: {config?.configured ? 'Configured' : 'Not configured'}
            </div>
            <div>
              Auth: {config?.authConfigured || config?.configured ? 'Configured, Admin Creds' : 'Pending'}
            </div>
          </div>
        </div>

        {!config?.configured && (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900 text-amber-800 dark:text-amber-300 p-3 text-sm">
            OSINT service endpoint is not configured. Add the service endpoint to Key Vault and restart KloudSecure.
          </div>
        )}
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300 p-4 text-sm">
          {error}
        </div>
      )}

      <div className="bg-white dark:bg-card rounded-xl shadow p-5">
        <h3 className="text-sm font-bold text-foreground mb-4">Start New Scan</h3>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
          {!hideCustomerSelector && (
            <div>
              <label className="block text-xs font-semibold text-muted-foreground mb-1">Customer</label>
              <select
                className="w-full border border-border bg-background text-foreground rounded-lg px-3 py-2 text-sm"
                value={customerId}
                onChange={(e) => {
                  setCustomerId(e.target.value);
                  setActiveCustomerId(e.target.value);
                }}
              >
                {customers.map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-muted-foreground mb-1">Target Type</label>
            <select
              className="w-full border border-border bg-background text-foreground rounded-lg px-3 py-2 text-sm"
              value={targetType}
              onChange={(e) => setTargetType(e.target.value)}
            >
              {TARGET_TYPES.map((item) => (
                <option key={item.value} value={item.value}>{item.label}</option>
              ))}
            </select>
          </div>

          <div className="lg:col-span-2">
            <label className="block text-xs font-semibold text-muted-foreground mb-1">Target</label>
            <input
              className="w-full border border-border bg-background text-foreground rounded-lg px-3 py-2 text-sm"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              placeholder={selectedCustomer?.monitoredDomains?.[0] ?? "example.com, 8.8.8.8, user@example.com"}
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-muted-foreground mb-1">Profile</label>
            <select
              className="w-full border border-border bg-background text-foreground rounded-lg px-3 py-2 text-sm"
              value={profile}
              onChange={(e) => setProfile(e.target.value)}
            >
              {PROFILES.map((item) => (
                <option key={item.value} value={item.value}>{item.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-4">
          <button
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
            onClick={() => void startScan()}
            disabled={starting || !target.trim() || !config?.enabled || !config?.configured || !canStartScan}
            title={!canStartScan ? 'Your role is not allowed to start OSINT scans' : undefined}
          >
            {starting ? 'Submitting...' : 'Start OSINT Scan'}
          </button>
        </div>
      </div>

      <div className="bg-white dark:bg-card rounded-xl shadow p-5">
        <div className="flex items-center justify-between gap-3 mb-4">
          <h3 className="text-sm font-bold text-foreground">
            Recent Scans {activeCustomer ? `· ${activeCustomer.name}` : ''}
          </h3>

          <button
            className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-border hover:bg-muted"
            onClick={() => void loadScans(customerId)}
          >
            Refresh List
          </button>
        </div>

        {loading ? (
          <p className="text-sm text-muted-foreground">Loading scans...</p>
        ) : scans.length === 0 ? (
          <p className="text-sm text-muted-foreground">No OSINT reconnaissance scans found for this customer.</p>
        ) : (
          <div className="overflow-auto border border-border rounded-xl">
            <table className="min-w-full text-sm">
              <thead className="bg-muted">
                <tr>
                  <th className="text-left px-3 py-2">Target</th>
                  <th className="text-left px-3 py-2">Type</th>
                  <th className="text-left px-3 py-2">Profile</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">Results</th>
                  <th className="text-left px-3 py-2">Created</th>
                  <th className="text-left px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {scans.map((scan) => (
                  <tr key={scan.id} className="border-t border-border">
                    <td className="px-3 py-2 font-mono text-xs">{scan.target}</td>
                    <td className="px-3 py-2">{scan.target_type}</td>
                    <td className="px-3 py-2">{scan.profile}</td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-1 rounded-full text-xs ${spiderFootStatusClass(scan.status)}`}>
                       {cleanSpiderFootStatus(scan.status)}
                      </span>
                      {scan.error && <div className="text-xs text-red-600 mt-1">{scan.error}</div>}
                    </td>
                    <td className="px-3 py-2">
                      <div>{scan.result_summary?.total ?? '—'}</div>
                      {scan.result_summary?.byType && (
                        <div className="text-xs text-muted-foreground mt-1">Top: {topCountsText(scan.result_summary.byType)}</div>
                      )}
                      {scan.ai_analyzed_at && (
                        <div className="text-xs text-muted-foreground mt-1">AI: {fmtTs(scan.ai_analyzed_at)}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {fmtTs(scan.created_at)}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-2">
{canViewResults && (
  <button
    className="text-xs px-2 py-1 rounded border border-border hover:bg-muted"
    onClick={() => void refreshScan(scan)}
  >
    Status
  </button>
)}

{canViewResults && (
  <button
    className="text-xs px-2 py-1 rounded border border-border hover:bg-muted"
    onClick={() => void loadResults(scan)}
  >
    Results
  </button>
)}

{canRunAiAnalysis && (
  <button
    className="text-xs px-2 py-1 rounded border border-border hover:bg-muted disabled:opacity-60"
    onClick={() => void runAiAnalysis(scan)}
    disabled={!scan.spiderfoot_scan_id || aiAnalyzingId === scan.id}
  >
    {aiAnalyzingId === scan.id ? 'Analyzing…' : 'AI Analyze'}
  </button>
)}

{canDownloadResults && (
  <button
    className="text-xs px-2 py-1 rounded border border-border hover:bg-muted"
    onClick={() => downloadResults(scan, 'csv')}
    disabled={!scan.spiderfoot_scan_id}
  >
    Download CSV
  </button>
)}

{canDownloadResults && (
  <button
    className="text-xs px-2 py-1 rounded border border-border hover:bg-muted"
    onClick={() => downloadResults(scan, 'json')}
    disabled={!scan.spiderfoot_scan_id}
  >
    JSON
  </button>
)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selectedResult && (
        <div className="bg-white dark:bg-card rounded-xl shadow p-5">
          <h3 className="text-sm font-bold text-foreground mb-3">Selected Scan Results</h3>

          {selectedResult.spiderfootUiUrl && (
            <a
              className="inline-block mb-3 px-3 py-2 rounded-lg text-xs font-semibold border border-border hover:bg-muted"
              href={selectedResult.spiderfootUiUrl}
              target="_blank"
              rel="noreferrer"
            >
              Open in OSINT service UI
            </a>
          )}

          {selectedResult.summary && (
            <div className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
              <div className="rounded-lg border bg-muted/30 p-3">
                <div className="font-semibold text-foreground">Total Rows</div>
                <div className="text-lg font-bold mt-1">{selectedResult.summary.total ?? '—'}</div>
              </div>
              <div className="rounded-lg border bg-muted/30 p-3">
                <div className="font-semibold text-foreground">Top Event Types</div>
                <div className="text-muted-foreground mt-1">{topCountsText(selectedResult.summary.byType)}</div>
              </div>
              <div className="rounded-lg border bg-muted/30 p-3">
                <div className="font-semibold text-foreground">Top Modules</div>
                <div className="text-muted-foreground mt-1">{topCountsText(selectedResult.summary.byModule)}</div>
              </div>
            </div>
          )}

          {(selectedResult.aiAnalysis || selectedResult.scan?.ai_analysis) && (
            <div className="mb-5 rounded-xl border bg-muted/30 p-4 space-y-4">
              {(() => {
                const analysis: SpiderFootAiAnalysis = selectedResult.aiAnalysis ?? selectedResult.scan?.ai_analysis;
                return (
                  <>
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                      <div>
                        <h4 className="text-sm font-bold text-foreground">AI OSINT Analysis</h4>
                        <p className="text-sm text-muted-foreground mt-1">{analysis.executiveSummary}</p>
                        <p className="text-xs text-muted-foreground mt-2">Model: {analysis.model ?? '—'} · Generated: {analysis.generatedAt ? fmtTs(analysis.generatedAt) : '—'}</p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${riskClass(analysis.riskLevel)}`}>{analysis.riskLevel.toUpperCase()}</span>
                        <span className="px-3 py-1 rounded-full text-xs font-bold bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300">Confidence {analysis.overallConfidence}%</span>
                      </div>
                    </div>

                    {analysis.keyFindings?.length ? (
                      <div>
                        <h5 className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2">Prioritized OSINT Findings</h5>
                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                          {analysis.keyFindings.slice(0, 8).map((finding, index) => (
                            <div key={`${finding.data}-${index}`} className="rounded-lg border bg-background p-3">
                              <div className="flex items-start justify-between gap-2">
                                <div className="font-semibold text-sm text-foreground break-all">{finding.title || finding.data}</div>
                                <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${riskClass(finding.riskLevel)}`}>{finding.riskLevel}</span>
                              </div>
                              <div className="text-xs text-muted-foreground mt-1">Type: {finding.type || '—'} · Module: {finding.module || '—'} · Confidence: {finding.confidence}%</div>
                              <div className="text-xs text-muted-foreground mt-2 break-all">{finding.data}</div>
                              <ul className="list-disc pl-5 text-xs text-muted-foreground mt-2 space-y-1">
                                {(finding.reasons ?? []).slice(0, 4).map((reason, idx) => <li key={idx}>{reason}</li>)}
                              </ul>
                              <div className="text-xs mt-2"><span className="font-semibold">Action:</span> {finding.recommendedAction}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}

                    {analysis.additionalCollectionTargets?.length ? (
                      <div>
                        <h5 className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2">AI-suggested OSINT pivots</h5>
                        <div className="overflow-x-auto rounded-lg border bg-background">
                          <table className="min-w-full text-xs">
                            <thead className="bg-muted/80 text-left uppercase tracking-wide text-muted-foreground">
                              <tr><th className="py-2 px-3">Target</th><th className="py-2 px-3">Type</th><th className="py-2 px-3">Profile</th><th className="py-2 px-3">Rationale</th></tr>
                            </thead>
                            <tbody>
                              {analysis.additionalCollectionTargets.slice(0, 12).map((item, index) => (
                                <tr key={`${item.target}-${index}`} className="border-t align-top">
                                  <td className="py-2 px-3 font-medium text-foreground break-all">{item.target}</td>
                                  <td className="py-2 px-3 text-muted-foreground">{item.targetType}</td>
                                  <td className="py-2 px-3 text-muted-foreground">{item.recommendedProfile}</td>
                                  <td className="py-2 px-3 text-muted-foreground">{item.rationale}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">These are advisory collection targets. Run a new OSINT scan before treating them as confirmed exposure.</p>
                      </div>
                    ) : null}

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 text-xs">
                      <div className="rounded-lg border bg-background p-3">
                        <h5 className="font-bold text-foreground mb-2">Exposure Themes</h5>
                        <ul className="list-disc pl-5 space-y-1 text-muted-foreground">{(analysis.exposureThemes ?? []).slice(0, 8).map((item, idx) => <li key={idx}>{item}</li>)}</ul>
                      </div>
                      <div className="rounded-lg border bg-background p-3">
                        <h5 className="font-bold text-foreground mb-2">Correlated Indicators</h5>
                        <ul className="list-disc pl-5 space-y-1 text-muted-foreground">{(analysis.correlatedIndicators ?? []).slice(0, 8).map((item, idx) => <li key={idx}>{item}</li>)}</ul>
                      </div>
                      <div className="rounded-lg border bg-background p-3">
                        <h5 className="font-bold text-foreground mb-2">Next Steps</h5>
                        <ul className="list-disc pl-5 space-y-1 text-muted-foreground">{(analysis.recommendedNextSteps ?? []).slice(0, 8).map((item, idx) => <li key={idx}>{item}</li>)}</ul>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          )}

          <pre className="text-xs overflow-auto max-h-[520px] bg-muted rounded-xl p-4">
            {JSON.stringify(selectedResult.summary ?? selectedResult, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}
